import { ApolloCache, DocumentNode, TypedDocumentNode } from '@apollo/client';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'next-i18next/pages';
import { useCallback } from 'react';

import { ConnectionError } from '@pflegia/types/common/enums/errors/connection-error.enum';
import { Match } from '@pflegia/types/common/match/match';
import { useToast } from '@pflegia/ui/contexts/toast.context';

type ErrorMessage = 'offerAlreadyDeclined' | 'offerAlreadyAccepted' | 'applicationAlreadySent' | 'somethingWentWrong';

const getErrorMessage = (match?: Match): ErrorMessage => {
  if (!match) {
    return 'offerAlreadyDeclined';
  } else if (match.connectionType === 'invitation' && match.connectionStatus === 'accepted') {
    return 'offerAlreadyAccepted';
  } else if (match.connectionType === 'application' && match.connectionStatus === 'pending') {
    return 'applicationAlreadySent';
  }
  return 'somethingWentWrong';
};

export interface UseDialogButtonActionsBaseProps {
  // ID of the job position
  jobPositionId: string;
  queries: {
    findMatchForJobPosition: TypedDocumentNode<{ findMatchForJobPosition: Match }>; // Query to find matches for a job position, used to check for existing matches if applying
    getCandidateConnections: DocumentNode; // Query to get candidate connections by type and status
  };
  /**
   * Mutations to accept, apply, decline and revoke a match
   */
  mutations: {
    accept: DocumentNode; // Mutation to accept an offer
    apply: DocumentNode; // Mutation to apply for a job
    decline: DocumentNode; // Mutation to decline an offer
    revokeMatch: DocumentNode; // Mutation to revoke a match
  };
  tracking?: {
    /**
     * Event names to be tracked
     */
    events: {
      accept?: string;
    };
    /**
     * Function to track events
     */
    track: (eventName: string, data?: Record<string, unknown>) => void;
  };
  callBacks?: {
    /**
     * Additional callback when closing the modal
     */
    handleClose?: () => void;
    /**
     * Additional callback when closing this modal
     */
    triggerSuggestions?: (trigger: 'apply' | 'accept' | 'decline', props: unknown) => void;
  };
  meta?: {
    /**
     * The type name of the potential job position, used to locate the match in the cache after a mutation.
     * @default 'JobPositionForCandidateDto' (the default type name in Pflegia, to be changed)
     */
    potentialJobPositionTypeName?: string;
  };
}

/**
 * @warning when over-riding the `onCompleted` callback, of either mutations, make sure to replicate the default behavior.
 */
export const useDialogButtonActionsBase = ({
  jobPositionId,
  queries,
  mutations,
  tracking,
  callBacks,
  meta,
}: UseDialogButtonActionsBaseProps) => {
  const { t } = useTranslation('job', { keyPrefix: 'dialogue' });
  const toast = useToast();
  const apolloClient = useApolloClient();

  const updateJobPositionMatch = useCallback(
    (cache: ApolloCache<object>, match: Match) => {
      cache.writeQuery({
        data: { findMatchForJobPosition: match },
        query: queries.findMatchForJobPosition,
        variables: { jobPositionId },
      });
      cache.modify({
        id: cache.identify({ id: jobPositionId, __typename: meta?.potentialJobPositionTypeName ?? 'JobPositionForCandidateDto' }),
        fields: {
          match: () => ({ __ref: `Match:${match.id}` }),
        },
      });
    },
    [jobPositionId, meta?.potentialJobPositionTypeName, queries.findMatchForJobPosition],
  );

  const [findMatchForJobPosition, { loading }] = useLazyQuery(queries.findMatchForJobPosition);

  const onError = (error: Error) => {
    if (error?.message === ConnectionError.MaxSentApplicationsPerDay) {
      toast?.error(t('sentApplicationsThresholdReached'));
    } else if (error?.message === ConnectionError.ScoringError) {
      toast?.error(t('scoringError'));
    } else {
      findMatchForJobPosition({
        variables: { jobPositionId },
      }).then(({ data }) => {
        toast?.error(t(getErrorMessage(data?.findMatchForJobPosition)));
        apolloClient.refetchQueries({ include: 'active' });
      });
    }
    callBacks?.handleClose?.();
  };

  const [applyJobPosition, { loading: applyingLoading }] = useMutation<{ match: Match }>(mutations.apply, {
    onError,
    onCompleted: () => {
      if (tracking?.events?.accept) tracking?.track(tracking.events.accept, { jobPositionId });
    },
    update: (cache, result) => {
      const match = result.data?.match;
      if (!match) return;
      updateJobPositionMatch(cache, match);
      callBacks?.triggerSuggestions?.('apply', { jobPositionId });
      invalidateCache(cache);
    },
  });

  const [declineJobPosition, { loading: declinedLoading }] = useMutation<{ declineOffer: Match }>(mutations.decline, {
    onError,
    onCompleted: () => {
      callBacks?.triggerSuggestions?.('decline', { jobPositionId });
    },
    update: (cache, result) => {
      const match = result.data?.declineOffer;
      if (!match) return;
      updateJobPositionMatch(cache, match);
      invalidateCache(cache);
    },
  });

  const [acceptJobPosition, { loading: acceptLoading }] = useMutation<{ acceptOffer: Match }>(mutations.accept, {
    onError,
    onCompleted: data => {
      const offerType = data?.acceptOffer?.offerType;
      if (tracking?.events.accept) {
        tracking?.track(tracking?.events.accept, {
          jobPositionId,
          ...(offerType && { offerType }),
        });
      }
      callBacks?.triggerSuggestions?.('accept', { jobPositionId });
    },
    update: (cache, result) => {
      const match = result.data?.acceptOffer;
      if (!match) return;
      updateJobPositionMatch(cache, match);
      invalidateCache(cache);
    },
  });

  const [revokeMatch, { loading: revokeMatchLoading }] = useMutation<{
    revokeMatch: Match;
  }>(mutations.revokeMatch, {
    onError,
    onCompleted: () => {
      callBacks?.triggerSuggestions?.('decline', { jobPositionId });
    },
  });

  const invalidateCache = useCallback((cache: ApolloCache<object>) => {
    cache.evict({
      fieldName: 'getCandidateConnections',
    });
    cache.gc();
  }, []);

  return {
    findMatchForJobPosition,
    applyJobPosition,
    declineJobPosition,
    acceptJobPosition,
    revokeMatch,
    loading: loading || applyingLoading || declinedLoading || acceptLoading || revokeMatchLoading,
  };
};
